import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

class MeditationReminderService {
  async initialize() {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Running on web - notifications require native platform');
      return false;
    }
    
    try {
      const permission = await LocalNotifications.requestPermissions();
      if (permission.display === 'granted') {
        console.log('✅ Notification permissions granted');
        return true;
      } else {
        console.log('⚠️ Notification permissions denied');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  }

  getTimeOfDay() {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  }

  getNotificationContent(userName, timeOfDay) {
    const notifications = {
      morning: [
        {
          title: '🌅 Good Morning, ' + userName,
          body: 'Start your day right with a 5-minute guided meditation. Set your intentions and find your calm.'
        },
        {
          title: '☀️ Morning Time, ' + userName,
          body: 'Ready to meditate? Take a few minutes to ground yourself before the day begins.'
        },
        {
          title: '✨ Rise & Shine, ' + userName,
          body: 'Begin your morning with mindful breathing. Your guided meditation is waiting for you.'
        },
        {
          title: '🧘‍♀️ Hey ' + userName,
          body: 'Good morning! Start your day with clarity and calm. Your meditation practice awaits.'
        }
      ],
      afternoon: [
        {
          title: '🌤️ Midday Break, ' + userName,
          body: 'Time to reset and recharge. Take 5 minutes for a guided meditation and restore your focus.'
        },
        {
          title: '⏸️ Hey ' + userName,
          body: 'Your afternoon could use some calm. Pause for a quick meditation and come back refreshed.'
        },
        {
          title: '🍃 Time to Recharge, ' + userName,
          body: 'Step away for a moment. A guided meditation will help you restore energy and clarity.'
        },
        {
          title: '☕ Afternoon Reset, ' + userName,
          body: 'Take a mindful break. Your guided meditation is ready to help you refocus and rebalance.'
        }
      ],
      evening: [
        {
          title: '🌙 Evening Wind Down, ' + userName,
          body: 'Let go of today\'s stress with a calming meditation. Take time to reflect and unwind.'
        },
        {
          title: '🌆 Good Evening, ' + userName,
          body: 'End your day peacefully. A guided meditation will help you release tension and find stillness.'
        },
        {
          title: '✨ Time to Relax, ' + userName,
          body: 'Close your day with gratitude and calm. Your evening meditation is ready for you.'
        },
        {
          title: '🕊️ Hey ' + userName,
          body: 'Settle into the evening with a peaceful meditation. Release what no longer serves you.'
        }
      ]
    };

    const timeNotifications = notifications[timeOfDay] || notifications.evening;
    return timeNotifications[Math.floor(Math.random() * timeNotifications.length)];
  }

  async scheduleDailyReminders(userName) {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Notifications only work on mobile devices');
      return false;
    }

    if (!userName || userName.trim() === '') {
      userName = 'there';
    }

    try {
      console.log(`🔔 Setting up meditation reminders for ${userName}...`);
      
      // Cancel any existing meditation notifications first
      await LocalNotifications.cancel({ 
        notifications: [{ id: 101 }, { id: 102 }, { id: 103 }] 
      });

      const notifications = [];
      const now = new Date();

      // ===================================
      // MORNING REMINDER (8:00 AM)
      // ===================================
      const morningTime = new Date(now);
      morningTime.setHours(8, 0, 0, 0);
      if (morningTime <= now) {
        morningTime.setDate(morningTime.getDate() + 1);
      }

      const morningContent = this.getNotificationContent(userName, 'morning');
      
      notifications.push({
        id: 101,
        title: morningContent.title,
        body: morningContent.body,
        schedule: { 
          at: morningTime,
          repeats: true,
          every: 'day'
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'meditation', 
          timeOfDay: 'morning',
          userName: userName
        }
      });

      console.log(`📅 Morning (8 AM): "${morningContent.title}"`);
      console.log(`   ${morningContent.body}`);

      // ===================================
      // AFTERNOON REMINDER (2:00 PM)
      // ===================================
      const afternoonTime = new Date(now);
      afternoonTime.setHours(14, 0, 0, 0);
      if (afternoonTime <= now) {
        afternoonTime.setDate(afternoonTime.getDate() + 1);
      }

      const afternoonContent = this.getNotificationContent(userName, 'afternoon');
      
      notifications.push({
        id: 102,
        title: afternoonContent.title,
        body: afternoonContent.body,
        schedule: { 
          at: afternoonTime,
          repeats: true,
          every: 'day'
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'meditation', 
          timeOfDay: 'afternoon',
          userName: userName
        }
      });

      console.log(`📅 Afternoon (2 PM): "${afternoonContent.title}"`);
      console.log(`   ${afternoonContent.body}`);

      // ===================================
      // EVENING REMINDER (8:00 PM)
      // ===================================
      const eveningTime = new Date(now);
      eveningTime.setHours(20, 0, 0, 0);
      if (eveningTime <= now) {
        eveningTime.setDate(eveningTime.getDate() + 1);
      }

      const eveningContent = this.getNotificationContent(userName, 'evening');
      
      notifications.push({
        id: 103,
        title: eveningContent.title,
        body: eveningContent.body,
        schedule: { 
          at: eveningTime,
          repeats: true,
          every: 'day'
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'meditation', 
          timeOfDay: 'evening',
          userName: userName
        }
      });

      console.log(`📅 Evening (8 PM): "${eveningContent.title}"`);
      console.log(`   ${eveningContent.body}`);

      // ===================================
      // SCHEDULE ALL NOTIFICATIONS
      // ===================================
      await LocalNotifications.schedule({ notifications });
      
      console.log('✅ Meditation reminders activated!');
      console.log(`   • User: ${userName}`);
      console.log('   • 3 daily reminders set');
      console.log('   • Times: 8 AM, 2 PM, 8 PM');
      
      return true;

    } catch (error) {
      console.error('❌ Failed to schedule meditation reminders:', error);
      console.error('   Error details:', error.message);
      return false;
    }
  }

  async cancelAllReminders() {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Running on web - no notifications to cancel');
      return;
    }
    
    try {
      await LocalNotifications.cancel({ 
        notifications: [{ id: 101 }, { id: 102 }, { id: 103 }] 
      });
      console.log('✅ All meditation reminders cancelled');
    } catch (error) {
      console.error('❌ Failed to cancel reminders:', error);
    }
  }

  // Check if reminders are currently scheduled
  async areRemindersScheduled() {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const pending = await LocalNotifications.getPending();
      const meditationNotifs = pending.notifications.filter(n => 
        n.id === 101 || n.id === 102 || n.id === 103
      );
      return meditationNotifs.length > 0;
    } catch (error) {
      console.error('Error checking scheduled notifications:', error);
      return false;
    }
  }
}

export default new MeditationReminderService();